import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, Users, CreditCard, Plus, Edit2, Trash2, 
  TrendingUp, Activity, DollarSign, RefreshCw, Gift,
  AlertTriangle, CheckCircle, XCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreditsDialogOpen, setIsCreditsDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  
  // Form states
  const [newSubscription, setNewSubscription] = useState({
    name: '',
    description: '',
    credits: 100,
    master_user_email: ''
  });
  
  const [creditAction, setCreditAction] = useState({
    amount: 0,
    description: ''
  });

  useEffect(() => {
    if (user?.is_super_admin) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [subsRes, overviewRes, transRes] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/admin/usage/overview`),
        axios.get(`${API}/admin/credits/transactions?limit=50`)
      ]);
      setSubscriptions(subsRes.data);
      setOverview(overviewRes.data);
      setTransactions(transRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!newSubscription.name) {
      toast.error('Subscription name is required');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/subscriptions`, newSubscription);
      toast.success('Subscription created successfully');
      setIsCreateDialogOpen(false);
      setNewSubscription({ name: '', description: '', credits: 100, master_user_email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedSubscription) return;
    
    try {
      await axios.put(`${API}/admin/subscriptions/${selectedSubscription.id}`, {
        name: selectedSubscription.name,
        description: selectedSubscription.description,
        status: selectedSubscription.status
      });
      toast.success('Subscription updated');
      setIsEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleAssignCredits = async () => {
    if (!selectedSubscription || !creditAction.amount) {
      toast.error('Amount is required');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/credits/assign`, {
        subscription_id: selectedSubscription.id,
        amount: creditAction.amount,
        description: creditAction.description || `Credits assigned by super admin`
      });
      toast.success(`Assigned ${creditAction.amount} credits`);
      setIsCreditsDialogOpen(false);
      setCreditAction({ amount: 0, description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign credits');
    }
  };

  const handleRefund = async (subscriptionId, amount) => {
    const reason = prompt('Enter refund reason:');
    if (!reason) return;
    
    try {
      await axios.post(`${API}/admin/credits/refund/${subscriptionId}?amount=${amount}&reason=${encodeURIComponent(reason)}`);
      toast.success(`Refunded ${amount} credits`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refund credits');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      suspended: 'bg-amber-100 text-amber-700 border-amber-200',
      cancelled: 'bg-rose-100 text-rose-700 border-rose-200'
    };
    return styles[status] || styles.active;
  };

  if (!user?.is_super_admin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Card className="p-8 text-center">
            <AlertTriangle size={48} className="mx-auto text-rose-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600">You need super admin privileges to access this page.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="super-admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Super Admin Dashboard
            </h1>
            <p className="text-slate-600">Manage subscriptions, credits, and platform usage</p>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-subscription-btn"
          >
            <Plus size={18} className="mr-2" />
            New Subscription
          </Button>
        </div>

        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="p-4 border border-indigo-200 bg-indigo-50">
              <div className="flex items-center gap-3">
                <Building2 size={24} className="text-indigo-600" />
                <div>
                  <p className="text-2xl font-bold text-indigo-700">{overview.total_subscriptions}</p>
                  <p className="text-xs text-indigo-600">Subscriptions</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-emerald-200 bg-emerald-50">
              <div className="flex items-center gap-3">
                <CheckCircle size={24} className="text-emerald-600" />
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{overview.active_subscriptions}</p>
                  <p className="text-xs text-emerald-600">Active</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-blue-200 bg-blue-50">
              <div className="flex items-center gap-3">
                <Users size={24} className="text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-700">{overview.total_users}</p>
                  <p className="text-xs text-blue-600">Users</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-amber-200 bg-amber-50">
              <div className="flex items-center gap-3">
                <CreditCard size={24} className="text-amber-600" />
                <div>
                  <p className="text-2xl font-bold text-amber-700">{overview.total_credits_in_system}</p>
                  <p className="text-xs text-amber-600">Total Credits</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-purple-200 bg-purple-50">
              <div className="flex items-center gap-3">
                <Activity size={24} className="text-purple-600" />
                <div>
                  <p className="text-2xl font-bold text-purple-700">{overview.total_assessments}</p>
                  <p className="text-xs text-purple-600">Assessments</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-rose-200 bg-rose-50">
              <div className="flex items-center gap-3">
                <TrendingUp size={24} className="text-rose-600" />
                <div>
                  <p className="text-2xl font-bold text-rose-700">{overview.total_audits}</p>
                  <p className="text-xs text-rose-600">Audits</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="subscriptions" className="w-full">
          <TabsList>
            <TabsTrigger value="subscriptions" data-testid="subscriptions-tab">
              <Building2 size={16} className="mr-2" />
              Subscriptions ({subscriptions.length})
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="transactions-tab">
              <DollarSign size={16} className="mr-2" />
              Credit Transactions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscriptions" className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin text-slate-400" size={24} />
              </div>
            ) : subscriptions.length > 0 ? (
              <div className="grid gap-4">
                {subscriptions.map((sub) => (
                  <Card key={sub.id} className="p-6 border border-slate-200" data-testid={`subscription-${sub.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-slate-900">{sub.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(sub.status)}`}>
                            {sub.status}
                          </span>
                        </div>
                        {sub.description && (
                          <p className="text-sm text-slate-600 mb-3">{sub.description}</p>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-slate-500">Credits</p>
                            <p className="text-lg font-bold text-amber-600">{sub.credits}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Users</p>
                            <p className="text-lg font-bold text-slate-900">{sub.user_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Master User</p>
                            <p className="text-sm font-medium text-slate-900">
                              {sub.master_user?.name || 'Not assigned'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Created</p>
                            <p className="text-sm text-slate-900">
                              {new Date(sub.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSubscription(sub);
                            setIsDetailsDialogOpen(true);
                          }}
                          data-testid={`view-${sub.id}`}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSubscription(sub);
                            setCreditAction({ amount: 0, description: '' });
                            setIsCreditsDialogOpen(true);
                          }}
                          className="border-amber-300 text-amber-700"
                          data-testid={`add-credits-${sub.id}`}
                        >
                          <Gift size={14} className="mr-1" />
                          Credits
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSubscription({ ...sub });
                            setIsEditDialogOpen(true);
                          }}
                          data-testid={`edit-${sub.id}`}
                        >
                          <Edit2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Building2 size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">No subscriptions yet</p>
                <Button
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                >
                  Create First Subscription
                </Button>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="transactions" className="mt-4">
            <Card className="p-6 border border-slate-200">
              {transactions.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {transactions.map((trans) => (
                    <div
                      key={trans.id}
                      className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          trans.amount > 0 ? 'bg-emerald-100' : 'bg-rose-100'
                        }`}>
                          {trans.amount > 0 ? (
                            <TrendingUp size={16} className="text-emerald-600" />
                          ) : (
                            <Activity size={16} className="text-rose-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{trans.description}</p>
                          <p className="text-xs text-slate-500">
                            {trans.transaction_type} • {new Date(trans.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          trans.amount > 0 ? 'text-emerald-600' : 'text-rose-600'
                        }`}>
                          {trans.amount > 0 ? '+' : ''}{trans.amount}
                        </p>
                        <p className="text-xs text-slate-500">Balance: {trans.balance_after}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign size={40} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-600">No transactions yet</p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Subscription Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Subscription</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Subscription Name *</Label>
                <Input
                  placeholder="Company Name"
                  value={newSubscription.name}
                  onChange={(e) => setNewSubscription({ ...newSubscription, name: e.target.value })}
                  data-testid="sub-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description..."
                  value={newSubscription.description}
                  onChange={(e) => setNewSubscription({ ...newSubscription, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Initial Credits</Label>
                <Input
                  type="number"
                  min="0"
                  value={newSubscription.credits}
                  onChange={(e) => setNewSubscription({ ...newSubscription, credits: parseInt(e.target.value) || 0 })}
                  data-testid="sub-credits-input"
                />
              </div>
              <div>
                <Label>Master User Email (optional)</Label>
                <Input
                  type="email"
                  placeholder="admin@company.com"
                  value={newSubscription.master_user_email}
                  onChange={(e) => setNewSubscription({ ...newSubscription, master_user_email: e.target.value })}
                />
                <p className="text-xs text-slate-500 mt-1">User must already be registered</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleCreateSubscription} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-create-sub">
                  Create Subscription
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Subscription Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Subscription</DialogTitle>
            </DialogHeader>
            {selectedSubscription && (
              <div className="space-y-4">
                <div>
                  <Label>Subscription Name</Label>
                  <Input
                    value={selectedSubscription.name}
                    onChange={(e) => setSelectedSubscription({ ...selectedSubscription, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={selectedSubscription.description || ''}
                    onChange={(e) => setSelectedSubscription({ ...selectedSubscription, description: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={selectedSubscription.status}
                    onValueChange={(v) => setSelectedSubscription({ ...selectedSubscription, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateSubscription} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Assign Credits Dialog */}
        <Dialog open={isCreditsDialogOpen} onOpenChange={setIsCreditsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Credits</DialogTitle>
            </DialogHeader>
            {selectedSubscription && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600">Subscription: <span className="font-medium text-slate-900">{selectedSubscription.name}</span></p>
                  <p className="text-sm text-slate-600">Current Balance: <span className="font-bold text-amber-600">{selectedSubscription.credits} credits</span></p>
                </div>
                <div>
                  <Label>Credits to Add</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="100"
                    value={creditAction.amount || ''}
                    onChange={(e) => setCreditAction({ ...creditAction, amount: parseInt(e.target.value) || 0 })}
                    data-testid="credits-amount-input"
                  />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Input
                    placeholder="Reason for credit assignment"
                    value={creditAction.description}
                    onChange={(e) => setCreditAction({ ...creditAction, description: e.target.value })}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setIsCreditsDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleAssignCredits} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="confirm-assign-credits">
                    <Gift size={16} className="mr-2" />
                    Assign Credits
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Subscription Details Dialog */}
        <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Subscription Details</DialogTitle>
            </DialogHeader>
            {selectedSubscription && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Name</p>
                    <p className="font-medium">{selectedSubscription.name}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Status</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(selectedSubscription.status)}`}>
                      {selectedSubscription.status}
                    </span>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-600">Credits</p>
                    <p className="text-2xl font-bold text-amber-700">{selectedSubscription.credits}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600">Users</p>
                    <p className="text-2xl font-bold text-blue-700">{selectedSubscription.user_count || 0}</p>
                  </div>
                </div>
                {selectedSubscription.master_user && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Master User</p>
                    <p className="font-medium">{selectedSubscription.master_user.name}</p>
                    <p className="text-sm text-slate-600">{selectedSubscription.master_user.email}</p>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleRefund(selectedSubscription.id, 10)}
                    className="flex-1"
                  >
                    Refund 10 Credits
                  </Button>
                  <Button variant="outline" onClick={() => setIsDetailsDialogOpen(false)} className="flex-1">
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SuperAdminDashboard;
